import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { useProducts } from "../../lib/products";

const FromPrice = () => {
  const prices = useProducts().map((p) => p.price).filter((n) => typeof n === "number");
  return <p className="font-display text-4xl font-extrabold text-[var(--gold)]" data-testid="hero-price">{prices.length ? `$${(m => (m % 1 ? m.toFixed(2) : m.toFixed(0)))(Math.min(...prices))}` : "Fresh"}</p>;
};

const ease = [0.16, 1, 0.3, 1];

const TiltImage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-12, 12]), { stiffness: 120, damping: 18 });
  const glow = useTransform([mx, my], ([x, y]) => `radial-gradient(420px circle at ${x * 100}% ${y * 100}%, rgba(255,255,255,0.35), transparent 60%)`);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset} ref={ref} className="relative">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative aspect-[4/5] w-full">
        <div className="arch absolute inset-0 overflow-hidden bg-[#F3EEDF] shadow-[0_40px_80px_-30px_rgba(27,42,28,0.35)]">
          <img src="/images/hero-veg.jpg" alt="Colourful fresh vegetables including capsicums, broccoli and kumara" className="photo-bright h-full w-full scale-110 object-cover" data-testid="hero-image" />
          <motion.div className="pointer-events-none absolute inset-0 mix-blend-soft-light" style={{ background: glow }} />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -left-6 bottom-10 rounded-2xl border border-[#1B2A1C]/10 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl sm:-left-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">From</p>
          <FromPrice />
        </div>
      </motion.div>
      <SpinBadge />
    </div>
  );
};

const SpinBadge = () => (
  <div className="absolute -right-3 -top-6 h-28 w-28 sm:-right-8 sm:h-32 sm:w-32">
    <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
      <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text className="fill-[#1B2A1C] font-mono text-[8.5px] uppercase tracking-[0.12em]">
        <textPath href="#c">Fresh fruit • Fresh veges • Nelson • </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 m-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#EAB308] text-[#0F1410]">
      <ArrowDownRight className="h-5 w-5" />
    </span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <div className="hero-glow pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 pb-20 sm:px-10 lg:grid-cols-12 lg:gap-8 lg:pb-28">
        <motion.div style={{ y: textY }} className="lg:col-span-7 lg:self-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-[var(--gold)]">
            Fruit & vege boxes — Nelson, NZ
          </motion.p>
          <h1 className="sr-only" data-testid="hero-title">Go Box — fresh fruit & vege boxes delivered in Nelson</h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 1, ease }} className="flex max-w-xl flex-col gap-8">
            <p className="text-lg leading-relaxed text-[var(--sage)] sm:text-xl lg:text-2xl">
              A box of fresh, seasonal fruit and veges, packed and delivered to your door around Nelson. One price. No fuss.
            </p>
            <button onClick={() => scrollToId("order")} className="btn-gold group self-start px-7 py-4 text-base" data-testid="hero-order-cta">
              Order now
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease }} className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none lg:pl-8">
          <TiltImage />
        </motion.div>
      </div>
    </section>
  );
};
